import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const EditLorry = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Form States
    const [formData, setFormData] = useState({
        id: id,
        lorry_number: "",
        driver_name: "",
        driver_phone: "",
        status: "1"
    });

    // Error States for individual field validation
    const [errors, setErrors] = useState({});

    // 1. Fetch existing data
    useEffect(() => {
        const fetchLorryData = async () => {
            dispatch(loadingTrue());
            try {
                const res = await Handler({
                    method: "GET",
                    url: `/lorry/view.php?id=${id}`
                });

                if (res?.success) {
                    const data = res.data?.lorry;
                    setFormData({
                        id: data.id,
                        lorry_number: data.lorry_number,
                        driver_name: data.driver_name,
                        driver_phone: data.driver_phone,
                        status: data.status
                    });
                     dispatch(loadingFalse());
                } else {
                    toast.error("Vehicle details not found!");
                    navigate("/vehicles");
                     dispatch(loadingFalse());
                }
            } catch (err) {
                toast.error("Error fetching vehicle information");
                 dispatch(loadingFalse());
            } finally {
                dispatch(loadingFalse());
            }
        };
        fetchLorryData();
    }, [id, dispatch, navigate]);

    // 🔥 Machan, Inga thaan separate validation logic
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        if (!formData.lorry_number || !formData.lorry_number.trim()) {
            formErrors.lorry_number = "Lorry number is required";
            isValid = false;
        }

        if (!formData.driver_name || !formData.driver_name.trim()) {
            formErrors.driver_name = "Driver name is required";
            isValid = false;
        }

        if (!formData.driver_phone) {
            formErrors.driver_phone = "Driver phone number is required";
            isValid = false;
        } else if (!/^[0-9]{10}$/.test(formData.driver_phone)) {
            formErrors.driver_phone = "Enter a valid 10-digit mobile number";
            isValid = false;
        }

        setErrors(formErrors);
        return isValid;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const finalValue = name === "lorry_number" ? value.toUpperCase().replace(/[^A-Z0-9]/g, '') : value;
        
        setFormData({ ...formData, [name]: finalValue });
        
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Trigger separate validation
        if (!validateForm()) {
            toast.error("Please fix the errors in the form.");
            return;
        }

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/lorry/update.php",
                data: formData
            });

            if (res?.success) {
                Swal.fire("Updated!", "Vehicle details updated successfully", "success");
                navigate("/vehicles");
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Update failed");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred during update", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const blockInvalidChar = (e) => ["-", "e", "E"].includes(e.key) && e.preventDefault();

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Logistics &gt; Fleet Management</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Edit Vehicle Details</h3>
                        <p className="text-muted">Update information for Lorry: <strong>{formData.lorry_number}</strong></p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fw-bold">Lorry Number *</Label>
                                    <InputGroup className={errors.lorry_number ? "is-invalid" : ""}>
                                        <InputGroupText className="bg-light"><i className="bx bxs-truck"></i></InputGroupText>
                                        <Input
                                            type="text"
                                            name="lorry_number"
                                            placeholder="e.g. TN33AA1234"
                                            className={errors.lorry_number ? "is-invalid" : ""}
                                            value={formData.lorry_number}
                                            onChange={handleChange}
                                        />
                                    </InputGroup>
                                    {errors.lorry_number && <div className="text-danger small mt-1">{errors.lorry_number}</div>}
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fw-bold">Operation Status</Label>
                                    <Input
                                        type="select"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="form-select-lg"
                                        style={{ height: "45px", borderRadius: "8px" }}
                                    >
                                        <option value="1">Active / On Duty</option>
                                        <option value="0">Inactive / Maintenance</option>
                                    </Input>
                                </FormGroup>
                            </Col>

                            <Col md={6} className="mt-3">
                                <FormGroup>
                                    <Label className="fw-bold">Driver Name *</Label>
                                    <Input
                                        type="text"
                                        name="driver_name"
                                        placeholder="Enter driver's name"
                                        className={`text-capitalize ${errors.driver_name ? "is-invalid" : ""}`}
                                        style={{ height: "45px", borderRadius: "8px" }}
                                        value={formData.driver_name}
                                        onChange={handleChange}
                                    />
                                    {errors.driver_name && <div className="invalid-feedback">{errors.driver_name}</div>}
                                </FormGroup>
                            </Col>

                            <Col md={6} className="mt-3">
                                <FormGroup>
                                    <Label className="fw-bold">Driver Phone *</Label>
                                    <InputGroup className={errors.driver_phone ? "is-invalid" : ""}>
                                        <InputGroupText><i className="bx bx-phone"></i></InputGroupText>
                                        <Input
                                            type="number"
                                            name="driver_phone"
                                            onKeyDown={blockInvalidChar}
                                            placeholder="10 digit number"
                                            className={errors.driver_phone ? "is-invalid" : ""}
                                            value={formData.driver_phone}
                                            onChange={handleChange}
                                        />
                                    </InputGroup>
                                    {errors.driver_phone && <div className="text-danger small mt-1">{errors.driver_phone}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                            <Button type="button" color="light" className="me-2" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="add-btn px-5">
                                <i className="bx bx-save me-1"></i> Update Lorry
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default EditLorry;